/**
 * T8014 · NATS heartbeat health signal.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §L1453 "every realistic failure has a named path" — NATS heartbeat miss
 * drives the FSM into DEGRADED_NO_NATS with `cause: 'heartbeat-miss'`.
 *
 * WHY 5 s pingInterval (override of NATS default 120 s)
 * -----------------------------------------------------
 * NATS-core's default pingInterval is 120 s. With the spec's
 * `missThreshold = 3`, that yields a 6-min detection latency for a silent
 * dead WS — ~70× the user-perceived "this app is broken" threshold. The SDK
 * overrides pingInterval to 5 s at connect-config so 3 misses (≈ 15 s) flip
 * `natsHealthy` to false within the user's tolerance window.
 *
 * STATUS EVENTS CONSUMED
 * ----------------------
 *   - `pingTimer`  · a scheduled ping went out without a pong before the next
 *     pingTimer tick. NATS-core fires this on each missed pong.
 *   - `ping`       · a ping completed round-trip (pong received). Resets
 *     `consecutiveMisses` to 0 and records a healthy tick.
 *   - `disconnect` · transport closed. Flips `natsHealthy = false` IMMEDIATELY
 *     (no threshold) — disconnect is unambiguous, no need to wait.
 *   - `reconnect`  · transport restored. Flips `natsHealthy = true` and
 *     records a tick. Spec L1453 reconnect path.
 *   - `close`      · nats-core gave up for good (auth abort after a token
 *     expiry, explicit close). Flips `natsHealthy = false`; the
 *     `SDKProvider` closed() watcher owns the rebuild.
 *
 * The function returns a cleanup closure that aborts the for-await loop so
 * HMR (L1283) can tear down the listener without leaking the iterator.
 */

export interface NatsStatusEvent {
  readonly type: 'ping' | 'pingTimer' | 'disconnect' | 'reconnect' | 'reconnecting' | 'close';
}

export interface NatsHeartbeatDeps {
  /** Returns an async iterable of status events from the NATS client. */
  readonly statusIterator: () => AsyncIterable<NatsStatusEvent>;
  /** Flip the natsHealthy signal in the connectivity store. */
  readonly setNatsHealthy: (healthy: boolean, cause: string) => void;
  /** Record a heartbeat tick for staleness telemetry. */
  readonly recordTick: () => void;
  /** Notify recovery owners after the NATS transport reconnects. */
  readonly onReconnect?: () => void;
  /** Consecutive `pingTimer` events before flipping unhealthy. Defaults to 3. */
  readonly missThreshold?: number;
}

/**
 * Default miss threshold per spec L1453. Three consecutive pingTimer events
 * at 5 s pingInterval → ≈ 15 s detection latency.
 */
export const DEFAULT_HEARTBEAT_MISS_THRESHOLD = 3;

/**
 * Install the heartbeat health-signal listener. Returns a cleanup function
 * that aborts the for-await loop.
 */
export function installNatsHeartbeat(deps: NatsHeartbeatDeps): () => void {
  const threshold = deps.missThreshold ?? DEFAULT_HEARTBEAT_MISS_THRESHOLD;
  let consecutiveMisses = 0;
  let aborted = false;

  void (async () => {
    for await (const ev of deps.statusIterator()) {
      if (aborted) return;
      switch (ev.type) {
        case 'pingTimer':
          consecutiveMisses += 1;
          if (consecutiveMisses >= threshold) {
            deps.setNatsHealthy(false, 'nats-heartbeat-miss');
          }
          break;
        case 'ping':
          consecutiveMisses = 0;
          deps.setNatsHealthy(true, 'nats-pong');
          deps.recordTick();
          break;
        case 'reconnect':
          consecutiveMisses = 0;
          deps.setNatsHealthy(true, 'nats-reconnect');
          deps.recordTick();
          deps.onReconnect?.();
          break;
        case 'disconnect':
          deps.setNatsHealthy(false, 'nats-disconnect');
          break;
        case 'close':
          deps.setNatsHealthy(false, 'nats-close');
          break;
        case 'reconnecting':
          // No state change — the FSM is already in a transitional state.
          // Reconnecting is informational; final verdict comes on
          // reconnect / disconnect.
          break;
      }
    }
  })().catch((): void => {
    // Iterator threw — propagate as unhealthy so the FSM degrades.
    if (!aborted) deps.setNatsHealthy(false, 'nats-iterator-threw');
  });

  return (): void => {
    aborted = true;
  };
}
